import { useEffect, useState } from "react";
import McKinneyHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in McKinney?", a: "We offer McKinney-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The exact timeline depends on your application type, document completeness, and current State Department processing volumes. We review your documents before submission to catch errors that cause delays." },
  { q: "Do I need to visit an office in McKinney?", a: "No. We do not have a physical office in McKinney. Our service is remote: you complete forms online, visit a local acceptance facility (like a McKinney post office or Collin County Clerk) for in-person verification, then ship documents to us via overnight courier. We handle the expedited submission at the State Department and return your passport to your McKinney address." },
  { q: "Where is the nearest passport acceptance facility in McKinney?", a: "McKinney has several acceptance facilities including the downtown post office, Collin County Clerk offices, and nearby locations in Allen, Prosper, and Frisco. We provide a curated list of McKinney-area facilities with address, phone, and photo availability. First-time applicants and minors must visit in person; renewals typically ship directly." },
  { q: "Can I get a same-day passport in McKinney?", a: "The nearest passport agency is in Dallas, which does not accept walk-ins without proof of imminent travel within 14 days and an appointment. Same-day processing is extremely rare. For faster-than-14-day needs, our registered courier service offers the fastest legally available channel — as quick as 24 hours with proper documentation." },
  { q: "How much does expedited passport service cost in McKinney?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee that covers document review, overnight shipping, and expedited submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. We offer a best price guarantee." },
  { q: "What documents do I need for a McKinney passport application?", a: "For a new adult passport: proof of citizenship (certified birth certificate or naturalization certificate), government-issued photo ID, one passport photo, and the completed DS-11 form. For renewals: your most recent passport, one photo, and the DS-82 form. For minors: both parents must appear (or provide consent), plus the child's birth certificate and photos. We provide a detailed checklist for McKinney clients." },
  { q: "Can I get a passport photo in McKinney?", a: "Yes. Many McKinney post offices, CVS, Walgreens, and Walmart locations offer passport photos. Some post offices include photo services with their acceptance facility hours. We recommend confirming photo availability before visiting. Passport photos must meet strict State Department specifications — 2x2 inches, white background, no glasses, neutral expression." },
  { q: "Do I need an appointment at McKinney passport acceptance facilities?", a: "Many McKinney post offices require appointments for passport acceptance. Some accept walk-ins during limited hours. We recommend calling ahead. Collin County Clerk offices and larger post offices typically offer more flexible appointment times. We can help you find the facility with the next available appointment." },
  { q: "Can I track my McKinney passport application?", a: "Yes. Once we submit your application, we provide tracking information. You can also check the State Department's online passport status system. Our courier service includes tracking at every stage: from McKinney to our processing center, through State Department review, and return shipping to your Texas address." },
  { q: "What if my passport is lost or stolen in McKinney?", a: "File a police report if stolen, then complete Form DS-64 (Statement Regarding Lost or Stolen Passport) along with Form DS-11 for a replacement. You will need to visit an acceptance facility in person and provide citizenship documentation again. If you have upcoming travel, our expedited service can process the replacement as fast as 24 hours." },
];

function McKinneyStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) {
            setActive(id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" },
    { id: "services", label: "Services" },
    { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" },
    { id: "destinations", label: "Destinations" },
    { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" },
    { id: "comparison", label: "Compare" },
    { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" },
    { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (
          <a key={l.id} href={`#${l.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"}`}>
            {l.label}
          </a>
        ))}
      </div>
    </div>
  );
}

function McKinneyProcessingTimeSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">McKinney Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">We offer multiple speed tiers for McKinney-area travelers. Choose the timeline that matches your departure urgency.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For McKinney travelers with imminent departure within 3 days. Includes same-day document review and overnight courier submission." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For business travelers and families with travel in 1–2 weeks. Balanced speed and value with full document review." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For McKinney residents planning ahead. Still faster than standard post office processing (10–13 weeks) with professional document handling." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function McKinneyServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for McKinney travelers with imminent departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for McKinney residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time application guidance for McKinney adults with acceptance facility support." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 form guidance for McKinney residents." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for McKinney families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-stack-line", title: "Second Passport", desc: "Second passport issuance for frequent McKinney business travelers." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors that delay McKinney applications." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">McKinney Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Comprehensive passport solutions for McKinney's growing community — from young professionals to families to students at Collin College.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function McKinneyLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for McKinney</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            McKinney is one of the fastest-growing cities in America, having more than doubled its population since 2010. Located 30 miles north of Dallas, it combines a charming historic downtown with booming suburban expansion that stretches across Collin County. From young professionals in the corporate corridors to families in master-planned communities like Stonebridge Ranch and Adriatica, McKinney residents travel internationally for work, education, and leisure at increasing rates.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            McKinney's growth has attracted a wave of corporate relocations and remote workers seeking more space than Dallas proper offers. The result is a community of young professionals and families who commute to Dallas-Fort Worth for work but enjoy McKinney's quality of life. These residents often book international travel on short notice — a last-minute business meeting in London, a spontaneous European vacation, or a family emergency overseas. The nearest passport agency is in Dallas, and appointment slots fill up weeks in advance, making remote expedited service essential.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Collin College and the nearby University of Texas at Dallas serve thousands of students from McKinney and surrounding communities. Study abroad programs, international internships, and research conferences create passport demand among young people who may be applying for their first passport. The academic calendar creates predictable but compressed timelines — a semester abroad confirmed in August for a September departure, a summer research program in May for a June start. Standard post office processing simply cannot accommodate these academic schedules.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            McKinney families are known for their active lifestyles and international travel. From spring break cruises to European summer vacations, from mission trips to Central America to adoption travel to Asia, McKinney's family-oriented community generates diverse passport needs. The challenge is that many families book travel with limited lead time, especially when deals appear or school schedules are confirmed. A family of four discovering their passports expired creates a logistical and financial crisis that requires fast, reliable expedited service.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            The McKinney National Airport and the nearby DFW International Airport provide McKinney residents with excellent air connectivity. DFW offers direct flights to over 200 international destinations, making it one of the best-connected airports in the world. But that connectivity means McKinney residents can book international travel on extremely short notice — and discover passport issues just as quickly. A weekend sale fare to Paris or Tokyo can trigger an urgent passport need with days, not weeks, to resolve.
          </p>
        </div>
      </div>
    </section>
  );
}

function McKinneyTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for McKinney Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">DFW International Airport</h3>
                <p className="text-gray-400 text-xs">30 miles south of McKinney, one of the world's busiest international hubs</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              DFW International Airport is just 30 miles south of McKinney and offers direct flights to over 200 international destinations across six continents. American Airlines' largest hub means McKinney residents can reach London, Tokyo, Sydney, Buenos Aires, and Dubai with a single connection. The airport's massive international connectivity creates a unique challenge: McKinney travelers can book flights on extremely short notice, but that also means passport validity issues are discovered at booking, not weeks before departure.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> DFW's international flights often require 6 months of passport validity. Check before booking a last-minute fare — not the morning of your flight.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Collin College &amp; Academic Travel</h3>
                <p className="text-gray-400 text-xs">Study abroad, international internships, and research conferences</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Collin College serves over 50,000 students from McKinney and surrounding communities. Study abroad programs, international internships, and research conferences create predictable but compressed passport timelines. A semester in Spain confirmed in August for a September departure, a nursing clinical rotation in the Philippines, or a business conference in Singapore — all require current passports with academic deadlines that cannot be moved.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Study abroad programs often require 6+ months of validity beyond your return date. Academic deadlines are immovable — plan your passport renewal at least 6 months before your program starts.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Galveston &amp; Caribbean Cruise Departures</h3>
                <p className="text-gray-400 text-xs">300 miles southeast of McKinney, popular cruise port</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              McKinney families frequently book Caribbean cruises departing from the Port of Galveston. The drive is manageable for a 7-day sailing, and the deals are often irresistible. But cruise bookings with 3–8 weeks of lead time create compressed passport timelines when first-time applicants or children are involved. A family of four discovering that two passports expired is a common spring break scenario in McKinney.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Even closed-loop cruises often require passports for children. Disney Cruise Line requires passports for all passengers regardless of age. Verify before booking.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-briefcase-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Corporate Travel from Collin County</h3>
                <p className="text-gray-400 text-xs">Dallas-Fort Worth corporate corridor with international clients</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              McKinney's corporate community includes professionals who work for companies with international clients and operations. From tech consultants to financial advisors to healthcare executives, the DFW corporate corridor generates significant international business travel. The short-notice nature of client meetings — a European client available only next week, an Asian supplier needing immediate inspection — creates passport demand that cannot wait for standard processing.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Corporate travel often requires visas with lead times of their own. A Schengen visa for Europe can take 2–4 weeks. Start your passport and visa processes simultaneously, not sequentially.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-home-heart-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">McKinney Family Travel &amp; International Adoption</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              McKinney's family-oriented community includes families who travel internationally for adoption, missionary work, and family heritage visits. These trips often have fixed, immovable dates — an adoption court date in Asia, a mission trip departure, a family reunion in Europe. The emotional and financial investment makes passport delays especially devastating. We understand these unique pressures and prioritize family travel cases.
            </p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function McKinneyDestinationsSection() {
  const destinations = [
    { name: "Mexico", context: "Cancun and Riviera Maya vacations, family heritage trips, medical tourism in Monterrey", urgency: "Spring break and summer bookings peak February through June", icon: "ri-map-pin-2-line", flag: "MX" },
    { name: "Europe", context: "London and Paris business travel, Italian and Spanish vacations, study abroad programs", urgency: "Business trips and academic travel often booked 2–4 weeks out", icon: "ri-map-pin-2-line", flag: "GB" },
    { name: "Caribbean &amp; Bahamas", context: "Cruise departures from Galveston, resort vacations, family spring break", urgency: "Cruise bookings and last-minute resort deals happen 2–4 weeks out", icon: "ri-map-pin-2-line", flag: "BS" },
    { name: "Asia", context: "Adoption travel, mission trips, business travel to China, Japan, and Southeast Asia", urgency: "Adoption and mission trip dates are fixed and cannot be moved", icon: "ri-map-pin-2-line", flag: "CN" },
    { name: "Central America", context: "Mission trips, eco-tourism, family heritage visits to Guatemala and Costa Rica", urgency: "Mission trip departures often have 4–8 week notice windows", icon: "ri-map-pin-2-line", flag: "CR" },
    { name: "Australia &amp; Pacific", context: "Vacation travel, business conferences, study abroad programs", urgency: "Long-haul flights require extra passport validity checks before booking", icon: "ri-map-pin-2-line", flag: "AU" },
  ];
  const commonSituations = [
    { stat: "40%", label: "of McKinney expedited requests are family or academic travel with 2–4 week notice" },
    { stat: "3–6 Weeks", label: "average notice McKinney travelers have before international departure or cruise sailing" },
    { stat: "Mar–Jun", label: "peak season when spring break, summer cruises, and academic travel coincide" },
    { stat: "48 Hours", label: "shortest turnaround we have accommodated for a McKinney executive with a London meeting" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where McKinney Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/frisco" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Frisco <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/plano" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Plano <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/dallas" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Dallas <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function McKinneyTestimonialsSection() {
  const testimonials = [
    { name: "Sarah K.", location: "Stonebridge Ranch", text: "We booked a European cruise and realized my husband's passport expired. Fast Passport Center got it renewed in 5 days. Our anniversary trip was saved.", rating: 5 },
    { name: "Michael T.", location: "Adriatica", text: "I had a last-minute client meeting in London with 8 days notice. They processed my passport in 3 days. The document review caught an issue I never would have noticed.", rating: 5 },
    { name: "The Patel Family", location: "Craig Ranch", text: "Our daughter's study abroad program in Spain was confirmed in July for a September start. They handled her first passport application and got it back with weeks to spare.", rating: 5 },
    { name: "Jessica W.", location: "Historic Downtown", text: "I've used them twice for family trips. Both times under 7 business days. The process is so easy — upload, review, ship, done. Highly recommend for busy families.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What McKinney Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>
                ))}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function McKinneyAcceptanceFacilitiesSection() {
  const facilities = [
    { name: "McKinney Main Post Office", address: "5500 W Eldorado Pkwy, McKinney, TX 75070", phone: "(972) 542-2023", photo: "Yes", appointment: "Required" },
    { name: "Collin County District Clerk", address: "2100 Bloomdale Rd, McKinney, TX 75071", phone: "(972) 548-4185", photo: "No", appointment: "Required" },
    { name: "Allen Post Office", address: "302 E Bethany Dr, Allen, TX 75002", phone: "(972) 727-2100", photo: "Yes", appointment: "Walk-in" },
    { name: "Prosper Post Office", address: "601 S Preston Rd, Prosper, TX 75078", phone: "(972) 347-6064", photo: "Yes", appointment: "Walk-in" },
    { name: "Frisco Main Post Office", address: "1400 Stonebrook Pkwy, Frisco, TX 75034", phone: "(972) 377-6680", photo: "Yes", appointment: "Required" },
    { name: "Plano Post Office", address: "1200 Jupiter Rd, Plano, TX 75074", phone: "(972) 423-2724", photo: "Yes", appointment: "Required" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">McKinney Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility in person. Here are McKinney-area options with hours and contact information.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`${f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}`}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. We recommend calling ahead to confirm availability. We can also help you find the facility with the earliest appointment slot.</p>
        </div>
      </div>
    </section>
  );
}

function McKinneyComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">McKinney Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Third-Party Resellers</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "7–14 days" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "Minimal or extra fee" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Often 30–100% markup" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Reseller, not registered" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "Often limited" },
                { feature: "Shipping Included", us: "Overnight both ways", post: "You pay shipping", reseller: "Varies by plan" },
                { feature: "McKinney-Specific Guidance", us: "Yes, local facility knowledge", post: "Generic", reseller: "Generic" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "Varies" },
                { feature: "Same-Day Submission", us: "Yes, for emergency tier", post: "No", reseller: "Rarely" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function McKinneyMistakesSection() {
  const mistakes = [
    { title: "Booking Flights Before Checking Passports", desc: "McKinney's proximity to DFW means residents can book international flights on extremely short notice. Many book first, then discover passport issues with days to spare." },
    { title: "Waiting Until the Last Minute", desc: "McKinney's post office appointments fill up fast, especially during spring break and summer. Waiting until 2 weeks before travel often leaves you with no available appointment slots." },
    { title: "Using the Wrong Form", desc: "McKinney applicants frequently confuse DS-11 (new/child) with DS-82 (renewal). Using the wrong form causes rejection and delays your entire timeline." },
    { title: "Unacceptable Passport Photos", desc: "Many McKinney CVS and Walgreens photo technicians are not trained on current State Department photo standards. A rejected photo adds 1–2 weeks to your processing time." },
    { title: "Not Checking Family Passport Validity", desc: "McKinney families often book group travel and forget to check every member's passport. One expired passport can ground the entire family." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common McKinney Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function McKinneyHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our McKinney-specific intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit a McKinney-area acceptance facility. We provide the nearest location with available appointments." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed application package via overnight courier to our processing center. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit your application through our registered courier channel with the State Department. You receive tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Your passport is delivered to your McKinney address via secure overnight shipping. Most McKinney clients receive it within their chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for McKinney Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function McKinneyFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">McKinney Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function McKinneyHelpfulResourcesSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for McKinney Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <a href="https://travel.state.gov/content/travel/en/passports.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-government-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">U.S. State Department</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Official passport information, forms, and processing status updates.</p>
          </a>
          <a href="https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-earth-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Travel Advisories</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Current safety and entry requirements for every country.</p>
          </a>
          <a href="https://www.cbp.gov/travel/travel-industry-persons/visa-waiver-program" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-visa-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Visa Waiver Program</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Check if your destination participates in the ESTA visa waiver program.</p>
          </a>
        </div>
      </div>
    </section>
  );
}

function McKinneyFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your McKinney Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">
          Whether you are flying out of DFW in 3 days, sailing on a Caribbean cruise in 3 weeks, or sending your student abroad next semester, we will get your passport processed on time. No surprises. No delays. Just fast, reliable service.
        </p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function McKinneyPage() {
  useEffect(() => {
    document.title = "Emergency Passport McKinney, TX — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/mckinney/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in McKinney, TX — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving McKinney families, students, and business travelers.");
    setMeta("geo.placename", "McKinney, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "33.1972;-96.6397");
    setMeta("ICBM", "33.1972, -96.6397");
    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for McKinney travelers with immediate departure needs." },
      { name: "Passport Renewal", description: "Fast passport renewal for McKinney residents with recent passport issuance." },
      { name: "New Passport", description: "First-time application assistance for McKinney adults and students." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for McKinney residents." },
      { name: "Child Passport", description: "Passport services for minors under 16 in McKinney." },
      { name: "Name Change", description: "Passport correction after marriage, divorce, or legal name changes." },
      { name: "Second Passport", description: "Second passport issuance for frequent McKinney business travelers." },
    ];
    const graph = [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
        { "@type": "ListItem", position: 2, name: "Texas", item: "https://fastpassportcenter.com/texas/" },
        { "@type": "ListItem", position: 3, name: "McKinney Passport Services", item: pageUrl },
      ]},
      { "@type": "LocalBusiness", name: "Fast Passport Center", description: "Fast Passport Center does not have a physical office in McKinney — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for McKinney and Collin County travelers.", url: pageUrl, telephone: "+1-877-253-0084", areaServed: { "@type": "City", name: "McKinney", containedInPlace: { "@type": "State", name: "Texas" } }, priceRange: "$$$", openingHours: ["Mo-Fr 09:00-18:00"], aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" }},
      ...servicesList.map((s) => ({ "@type": "Service", serviceType: s.name, provider: { "@type": "LocalBusiness", name: "Fast Passport Center" }, areaServed: { "@type": "City", name: "McKinney" }, description: s.description })),
      { "@type": "FAQPage", mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <McKinneyHeroSection />
      <McKinneyStickyNav />
      <McKinneyProcessingTimeSection />
      <McKinneyServicesSection />
      <McKinneyLocalAreaSection />
      <McKinneyTravelTimingSection />
      <McKinneyDestinationsSection />
      <McKinneyTestimonialsSection />
      <McKinneyAcceptanceFacilitiesSection />
      <McKinneyComparisonSection />
      <McKinneyMistakesSection />
      <McKinneyHowItWorksSection />
      <McKinneyFAQSection />
      <McKinneyHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/mckinney" state="TX" />
      <McKinneyFinalCTA />
    </div>
  );
}